import {
  Button,
  ErrorBoundary,
  Icon,
  Shadow,
  Table,
  TableBody,
  TableHead,
  TableSkeleton,
  TableTitle,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlatList } from 'react-native';
import { v4 as uuidv4 } from 'uuid';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  CustomerDetailStackParamList,
} from '../../../../../../routes/types';
import { customerDetailSentOrdersQuery } from '../../../../../../store/customerDetail/selector';
import { CustomerSentOrdersResponseNormalized } from '../../../../../../store/customerDetail/types';

import { rowHeight, tableHead, tableHeadAlignments, widthCols } from './constants';
import { EmptyOrders } from './EmptyOrders';
import { SentOrderItem } from './SentOrderItem';

export const SentOrders: React.FC = () => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState<CustomerSentOrdersResponseNormalized[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { data, loading, error, refresher } = useRecoilQuery(
    customerDetailSentOrdersQuery({ customerId: route.params.customerId, page }),
    { cachePolicy: 'no-cache' }
  );

  const handleUpdateOrders = useCallback(() => {
    setPage(1);
    setAllData([]);
    setRefreshing(true);
  }, []);

  const handleSelect = useCallback(
    (order: CustomerSentOrdersResponseNormalized) => {
      navigate('OrderHistoryModal', { item: order });
    },
    [navigate]
  );

  const handleEndReached = useCallback(() => {
    if (loading || data?.meta.pagination.page !== page || error) {
      return;
    }

    if (data?.meta.pagination.hasNext) {
      setPage((prev) => prev + 1);
    }
  }, [loading, data, page, error]);

  const viewportType = useBreakpointValue({
    sm: 'tablet',
    lg: 'desktop',
  });

  const footerContent = useMemo(() => {
    return (
      <>
        {isLoading &&
          Array.from({ length: 1 }).map(() => (
            <TableBody key={uuidv4()} testId="loading-table-body">
              {Array.from({ length: Object.values(widthCols).length }).map((_, i) => (
                <TableSkeleton
                  key={uuidv4()}
                  color="brand.primary.lightest"
                  width={Object.values(widthCols)[i][viewportType]}
                />
              ))}
            </TableBody>
          ))}
      </>
    );
  }, [isLoading, viewportType]);

  const Wrapper = useBreakpointValue({
    base: React.Fragment,

    lg: ({ children }) => (
      <Shadow level={1} borderRadius={BoxRadius.sm} padding={Spacing['inset.xs']}>
        {children}
      </Shadow>
    ),
  });

  const tableHeadContent = useMemo(() => {
    return (
      <TableHead display={{ base: 'none', lg: 'flex' }} testId="table-order-history-head">
        {Object.entries(tableHead).map((item, index) => {
          return (
            <TableTitle
              key={item[0]}
              testId={`table-order-history-title-${index}`}
              width={widthCols[item[0]][viewportType]}
              textAlign={tableHeadAlignments[item[0]]}>
              {item[1] as string}
            </TableTitle>
          );
        })}
      </TableHead>
    );
  }, [viewportType]);

  const orderListContent = useMemo(() => {
    if (error) {
      return <ErrorBoundary onRefresh={handleUpdateOrders} />;
    }

    return (
      <Table
        testID="table-order-history-head"
        isLoading={loading && page === 1}
        cols={9}
        rows={3}
        rowHeight={rowHeight[viewportType]}>
        {tableHeadContent}
        <FlatList
          style={{ marginHorizontal: -Utils.Token.ToInt(Spacing, 'inset.xs'), maxHeight: 500 }}
          data={allData}
          windowSize={10}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={5}
          keyExtractor={(_, index) => index.toString()}
          showsVerticalScrollIndicator={false}
          onEndReachedThreshold={1.5}
          onEndReached={handleEndReached}
          ListFooterComponent={footerContent}
          ListEmptyComponent={<EmptyOrders isLoading={isLoading} data={allData} />}
          renderItem={({ item }) => <SentOrderItem order={item} onSelect={handleSelect} />}
        />
      </Table>
    );
  }, [
    page,
    allData,
    error,
    footerContent,
    handleEndReached,
    handleSelect,
    isLoading,
    loading,
    handleUpdateOrders,
    tableHeadContent,
    viewportType,
  ]);

  useEffect(() => {
    const items = data?.data;

    if (!loading && items?.length) {
      setAllData((prev) => [...prev, ...items]);
    }

    setIsLoading(loading);
  }, [loading, data, setAllData, setIsLoading]);

  useEffect(() => {
    if (data) {
      setUpdatedAt(new Date());
    }
  }, [data]);

  React.useEffect(() => {
    if (refreshing) {
      refresher();
      setRefreshing(false);
    }
  }, [refreshing, refresher]);

  return (
    <Wrapper>
      <VStack space={{ base: Spacing['stack.xxxs'], lg: Spacing['stack.xs'] }}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize={Typography['fontsize.lg']} variant="bold">
            Ordens Enviadas
          </Text>
          {!error && (
            <VStack space={Spacing['stack.quarck']} alignItems="flex-end">
              <Button
                variant="textLink"
                isLoading={loading}
                padding={0}
                rightIcon={
                  <Icon
                    name="Refresh"
                    size="xxs"
                    testID="update-button-icon"
                    color={Color['brand.tertiary.principal']}
                  />
                }
                onPress={handleUpdateOrders}>
                Atualizar
              </Button>
              <Text isLoading={loading} fontSize={Typography['fontsize.xs']} color="neutral.medium">
                Atualizado em {Utils.Mask.DateFormat(updatedAt, 'dd/MM hh:mm:ss')}
              </Text>
            </VStack>
          )}
        </HStack>
        {orderListContent}
      </VStack>
    </Wrapper>
  );
};
